import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Clock, Truck, AlertTriangle, CheckCircle2, Loader2, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = typeof window !== 'undefined' ? window.L : null;

const STATUS_LABEL = { assigned: 'Dispatched', at_shipper: 'At Shipper', in_transit: 'In Transit', at_receiver: 'At Receiver', delivered: 'Delivered', cancelled: 'Cancelled' };
const fmtClock = (iso) => (iso ? new Date(iso).toLocaleString([], { weekday: 'short', hour: 'numeric', minute: '2-digit' }) : '—');
const fmtAgo = (iso) => {
  if (!iso) return '—';
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : `${Math.floor(m / 60)}h ago`;
};
const fmtDur = (min) => (min == null ? '—' : min < 60 ? `${min} min` : `${Math.floor(min / 60)}h ${min % 60}m`);

const TrackMap = ({ data }) => {
  const ref = useRef(null);
  const map = useRef(null);
  const layers = useRef({});
  useEffect(() => {
    if (!L || !ref.current || map.current) return;
    map.current = L.map(ref.current, { zoomControl: false }).setView([39.5, -98.35], 4);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap', maxZoom: 19 }).addTo(map.current);
  }, []);
  useEffect(() => {
    const m = map.current;
    if (!L || !m || !data) return;
    const g = layers.current;
    const bounds = [];
    const color = data.company?.primary_color || '#002FA7';
    const pin = (key, coord, html, size) => {
      if (!coord) return;
      const ll = [coord[0], coord[1]];
      bounds.push(ll);
      const icon = L.divIcon({ className: '', html, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
      if (g[key]) g[key].setLatLng(ll); else g[key] = L.marker(ll, { icon }).addTo(m);
    };
    pin('pickup', data.pickup?.coord, `<div style="background:#fff;border:3px solid ${color};border-radius:50%;width:16px;height:16px"></div>`, 16);
    pin('delivery', data.delivery?.coord, `<div style="background:#16a34a;border:3px solid #fff;border-radius:50%;width:18px;height:18px;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`, 18);
    if (data.position) pin('truck', [data.position.lat, data.position.lng], `<div style="background:${color};color:#fff;border:2px solid #fff;border-radius:50%;width:30px;height:30px;display:flex;align-items:center;justify-content:center;font-size:14px;box-shadow:0 1px 6px rgba(0,0,0,.45)">🚚</div>`, 30);
    const geom = data.eta?.route_geometry;
    if (geom && geom.length > 1) {
      if (g.route) g.route.setLatLngs(geom); else g.route = L.polyline(geom, { color: '#16a34a', weight: 4, opacity: 0.75, dashArray: '8 8' }).addTo(m);
    }
    if (bounds.length) { try { m.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 }); } catch {} }
  }, [data]);
  return <div ref={ref} className="w-full h-[46vh] md:h-full md:min-h-[60vh]" data-testid="public-track-map" />;
};

export default function PublicTrack() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/public/track/${token}`); setData(r.data); setErr(''); }
    catch (e) { setErr(e?.response?.data?.detail || 'This tracking link is not available.'); }
  }, [token]);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);
  useEffect(() => { if (data?.load_ref) document.title = `Load ${data.load_ref} — Live ETA`; }, [data?.load_ref]);

  if (err) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="public-track-error">
      <div className="bg-white border border-slate-200 rounded-xl p-8 text-center max-w-md">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Tracking link unavailable</h1>
        <p className="text-sm text-slate-600 mt-2">{err}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading live ETA…</div>;

  const color = data.company?.primary_color || '#002FA7';
  const delivered = data.status === 'delivered';
  const late = data.late_minutes != null && data.late_minutes >= 30;
  const logo = data.company?.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${data.company.logo_url}` : null;

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="public-track-page">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
        {logo ? <img src={logo} alt="" className="h-9 w-auto object-contain" /> : <Truck className="w-7 h-7" style={{ color }} />}
        <div className="min-w-0">
          <div className="font-bold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="public-track-company">{data.company?.name || 'Carrier'}</div>
          <div className="text-xs text-slate-500">Live load tracking · Load <span className="font-semibold text-slate-700" data-testid="public-track-load-ref">{data.load_ref}</span></div>
        </div>
        {data.company?.phone && <a href={`tel:${data.company.phone}`} className="ml-auto text-xs inline-flex items-center gap-1 text-slate-600"><Phone className="w-3.5 h-3.5" /> {data.company.phone}</a>}
      </header>

      <div className="max-w-5xl mx-auto md:grid md:grid-cols-5 md:gap-4 md:p-4">
        <div className="md:col-span-3 md:rounded-xl overflow-hidden border-b md:border border-slate-200 bg-white">
          <TrackMap data={data} />
        </div>
        <div className="md:col-span-2 p-4 md:p-0 space-y-3">
          <div className={`rounded-xl p-4 text-white ${delivered ? 'bg-emerald-600' : ''}`} style={delivered ? {} : { background: color }} data-testid="public-track-status-card">
            <div className="text-xs uppercase tracking-widest opacity-80">Status</div>
            <div className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Work Sans, sans-serif' }} data-testid="public-track-status">{STATUS_LABEL[data.status] || data.status}</div>
            {delivered ? (
              <div className="mt-2 text-sm flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Delivered {fmtClock(data.delivered_at)}</div>
            ) : data.eta ? (
              <div className="mt-3">
                <div className="text-xs uppercase tracking-widest opacity-80">Estimated arrival at {data.next_stop?.kind}</div>
                <div className="text-3xl font-bold" data-testid="public-track-eta">{fmtClock(data.eta.eta_at)}</div>
                <div className="text-sm opacity-90 mt-0.5 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {data.eta.distance_miles} mi · {fmtDur(data.eta.eta_minutes)} {data.eta.eta_source === 'road' ? 'by road' : '(estimate)'}</div>
              </div>
            ) : (
              <div className="mt-2 text-sm opacity-90">ETA will appear once the driver starts sharing location.</div>
            )}
          </div>
          {late && !delivered && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="public-track-late">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Running about {fmtDur(data.late_minutes)} behind the {fmtClock(data.appointment_at)} appointment.
            </div>
          )}
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <span className="mt-1 w-3 h-3 rounded-full border-2 shrink-0" style={{ borderColor: color }} />
              <div><div className="text-xs text-slate-500 uppercase tracking-widest">Pickup</div><div className="text-sm font-semibold text-slate-900">{data.pickup?.label}</div>{data.pickup?.date && <div className="text-xs text-slate-500">{data.pickup.date}{data.pickup.time ? ` · ${data.pickup.time}` : ''}</div>}</div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-1 w-3 h-3 rounded-full bg-emerald-600 shrink-0" />
              <div><div className="text-xs text-slate-500 uppercase tracking-widest">Delivery</div><div className="text-sm font-semibold text-slate-900">{data.delivery?.label}</div>{data.delivery?.date && <div className="text-xs text-slate-500">{data.delivery.date}{data.delivery.time ? ` · ${data.delivery.time}` : ''}</div>}</div>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 text-sm text-slate-700 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><Truck className="w-4 h-4 text-slate-500" /></div>
            <div>
              <div className="font-semibold">Driver {data.driver_first_name}</div>
              <div className="text-xs text-slate-500 flex items-center gap-1" data-testid="public-track-last-seen">
                <MapPin className="w-3 h-3" />
                {data.position ? (data.position.live ? <span className="text-emerald-700 font-medium">Live · updated {fmtAgo(data.position.updated_at)}</span> : `Last seen ${fmtAgo(data.position.updated_at)}`) : 'Location not shared yet'}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 text-center">Updates automatically every 30 seconds · Powered by CivilX TMS</p>
        </div>
      </div>
    </div>
  );
}
